/**
 * Patch a confirmation card's persisted resolution into the live message buffer
 * the moment the user resolves it.
 *
 * Ui cards bugs (#507). Confirm / Decline / Cancel on a chat card saves the
 * outcome server-side (`PATCH …/card-resolution` merges `{status[, count]}` into
 * `message_metadata[card_key].resolution`), but `<ResolutionCard>` deliberately
 * never writes back into its payload — so the warm `convMessagesRef` buffer kept
 * the PRE-confirmation payload. Switching to another chat and back repaints from
 * that buffer: the card mounted as `pending` (buttons live) until the
 * conversation re-fetch, 3–5 s later on staging, swapped in the resolved copy.
 * Patching the buffer makes the buffered copy the truth, so re-entry paints the
 * final state immediately.
 *
 * Mirrors the backend merge (`_apply_card_resolution`): a card_key with an array
 * slot (`calendar_event_created` → `calendar_events_created`, Calendar Ui #226;
 * `contact_creation_request` → `contact_creation_requests`, #515) resolves the
 * entry whose id (`event_id`; `card_id`/`action_id`) matches `entryId`, else the
 * scalar slot.
 *
 * Pure: returns a NEW messages array only when something changed, and never
 * mutates its input (buffer entries are shared with rendered React state).
 */

// Same as CARD_RESOLUTION_MAP's `array_key` + `entry_id_fields` entries
// (BE app/services/card_resolution.py). #515: contact cards accumulate too and
// are identified by their `card_id` (or ledger `action_id`).
export const ARRAY_KEYS = {
    calendar_event_created: { arrayKey: 'calendar_events_created', idFields: ['event_id'] },
    contact_creation_request: { arrayKey: 'contact_creation_requests', idFields: ['card_id', 'action_id'] },
    // #536: several invite cards in one turn.
    event_invite_request: { arrayKey: 'event_invite_requests', idFields: ['card_id', 'action_id'] },
};

const isEntry = (e, idFields, entryId) => !!e && typeof e === 'object'
    && idFields.some((f) => e[f] != null && e[f] !== '' && String(e[f]) === String(entryId));

function patchMessage(msg, { cardKey, entryId, resolution }) {
    const meta = msg?.metadata;
    if (!meta || typeof meta !== 'object') return msg;

    const spec = ARRAY_KEYS[cardKey];
    const arrayKey = spec?.arrayKey;
    if (entryId && arrayKey && Array.isArray(meta[arrayKey])) {
        let hit = false;
        const arr = meta[arrayKey].map((e) => {
            if (!hit && isEntry(e, spec.idFields, entryId)) {
                hit = true;
                return { ...e, resolution };
            }
            return e;
        });
        if (hit) {
            const nextMeta = { ...meta, [arrayKey]: arr };
            // The scalar slot (= the FIRST card) is the same card when it matches.
            const scalar = meta[cardKey];
            if (isEntry(scalar, spec.idFields, entryId)) {
                nextMeta[cardKey] = { ...scalar, resolution };
            }
            return { ...msg, metadata: nextMeta };
        }
    }

    const card = meta[cardKey];
    if (!card || typeof card !== 'object') return msg;
    return { ...msg, metadata: { ...meta, [cardKey]: { ...card, resolution } } };
}

/**
 * @param {object[]} messages one conversation's buffered UI messages
 * @param {{messageId: string, cardKey: string, entryId?: string, resolution: {status: string, count?: number}}} change
 * @returns {object[]} the same array when no message matched, otherwise a patched copy
 */
export function patchCardResolution(messages, change) {
    if (!Array.isArray(messages) || !change?.messageId || !change?.cardKey || !change?.resolution) {
        return messages;
    }
    const id = String(change.messageId);
    let changed = false;
    const next = messages.map((m) => {
        if (!m || (String(m.dbId ?? '') !== id && String(m.id ?? '') !== id)) return m;
        const patched = patchMessage(m, change);
        if (patched !== m) changed = true;
        return patched;
    });
    return changed ? next : messages;
}

export default patchCardResolution;

// A recorded decision that closes a card (#517) — anything but the Rulebook
// card's non-terminal `editing`. Mirrors BE `card_resolution.is_final_resolution`.
const isFinal = (resolution) => !!resolution?.status && resolution.status !== 'editing';

const idOf = (e, idFields) => {
    if (!e || typeof e !== 'object') return null;
    for (const f of [...idFields, 'action_id']) {
        if (e[f] != null && e[f] !== '') return String(e[f]);
    }
    return null;
};

const sameCard = (a, b, idFields) => {
    if (!a || !b) return false;
    const ids = new Set([...idFields, 'action_id'].map((f) => a[f]).filter((v) => v != null && v !== '').map(String));
    return [...idFields, 'action_id'].some((f) => b[f] != null && b[f] !== '' && ids.has(String(b[f])));
};

/**
 * The cards of one kind a message renders (#515 array, else the legacy scalar),
 * each ONCE and in its FINAL state when one is recorded (#517):
 *   - an entry repeated in the array (same card id) renders once — a final
 *     decision on any copy wins;
 *   - an entry still pending whose scalar mirror (= the first card) carries a
 *     final decision adopts it — messages written before #517 could hold the
 *     decision on the scalar only, which re-armed the rendered card on reload.
 *
 * @param {object} metadata a message's metadata
 * @param {string} cardKey e.g. 'contact_creation_request'
 * @returns {{payload: object, entryId: string|null, key: string}[]}
 */
export function cardEntriesForMessage(metadata, cardKey) {
    if (!metadata || typeof metadata !== 'object') return [];
    const spec = ARRAY_KEYS[cardKey];
    const scalar = metadata[cardKey] && typeof metadata[cardKey] === 'object' ? metadata[cardKey] : null;
    const arr = spec && Array.isArray(metadata[spec.arrayKey]) && metadata[spec.arrayKey].length
        ? metadata[spec.arrayKey] : null;
    if (!arr) {
        return scalar ? [{ payload: scalar, entryId: null, key: idOf(scalar, spec?.idFields || []) || `${cardKey}-0` }] : [];
    }
    const out = [];
    const byId = new Map();
    arr.forEach((e, i) => {
        if (!e || typeof e !== 'object') return;
        const id = idOf(e, spec.idFields);
        let payload = e;
        if (!isFinal(e.resolution) && isFinal(scalar?.resolution) && sameCard(scalar, e, spec.idFields)) {
            payload = { ...e, resolution: scalar.resolution };
        }
        if (id && byId.has(id)) {
            const prev = out[byId.get(id)];
            if (!isFinal(prev.payload.resolution) && isFinal(payload.resolution)) {
                out[byId.get(id)] = { ...prev, payload };
            }
            return;
        }
        if (id) byId.set(id, out.length);
        out.push({ payload, entryId: id, key: id || `${cardKey}-${i}` });
    });
    return out;
}

/**
 * #545 Obsolete confirmation cards are replaced. When PAI answers a correction
 * with a NEW card, the backend closes the request's earlier open card(s) and
 * streams `cards_superseded` `{action_ids, cards: [{action_id, card_key,
 * resolution}]}` (also on the live feed for a Telegram turn). Patch every
 * buffered message carrying one of those cards — scalar slots and array entries,
 * matched by the card's ledger `action_id` — with the stamped resolution
 * (`{status: <negative>, superseded: true}`), so the card loses its buttons at
 * once; a reload reads the same resolution from the persisted metadata.
 *
 * A card already holding a FINAL decision is never overwritten (#517). Pure:
 * returns the same array when nothing matched.
 *
 * @param {object[]} messages one conversation's buffered UI messages
 * @param {{action_ids?: string[], cards?: {action_id: string, card_key?: string, resolution?: object}[]}} change
 * @returns {object[]}
 */
export function patchSupersededCards(messages, change) {
    if (!Array.isArray(messages) || !change) return messages;
    const byId = new Map();
    for (const c of change.cards || []) {
        if (c?.action_id) byId.set(String(c.action_id), c.resolution || null);
    }
    for (const id of change.action_ids || []) {
        if (id && !byId.has(String(id))) byId.set(String(id), null);
    }
    if (!byId.size) return messages;
    const resolutionFor = (id) => ({ status: 'cancelled', ...(byId.get(id) || {}), superseded: true });
    const retire = (card) => {
        if (!card || typeof card !== 'object' || Array.isArray(card)) return card;
        const id = card.action_id != null ? String(card.action_id) : null;
        if (!id || !byId.has(id) || isFinal(card.resolution)) return card;
        return { ...card, resolution: resolutionFor(id) };
    };
    let changed = false;
    const next = messages.map((m) => {
        const meta = m?.metadata;
        if (!meta || typeof meta !== 'object') return m;
        let nextMeta = null;
        for (const [key, value] of Object.entries(meta)) {
            let patched = value;
            if (Array.isArray(value)) {
                const arr = value.map(retire);
                if (arr.some((e, i) => e !== value[i])) patched = arr;
            } else {
                patched = retire(value);
            }
            if (patched !== value) {
                nextMeta = nextMeta || { ...meta };
                nextMeta[key] = patched;
            }
        }
        if (!nextMeta) return m;
        changed = true;
        return { ...m, metadata: nextMeta };
    });
    return changed ? next : messages;
}
